import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useCallback, useState, type ReactElement } from 'react'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import Pill from '../Pill'
import SectionTitle from '../SectionTitle'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { discrepanciesApi, DiscrepancyCajaDto, DiscrepancyEquipoDto, EquipoEstado } from '../../api'
import { ToneKey } from '../../models'


const ESTADO: Record<EquipoEstado, { tone: ToneKey, label: string }> = {
  recibido: { tone: 'green', label: 'Recibido' },
  discrepancia: { tone: 'rosa', label: 'No llegó' },
  pendiente: { tone: 'amber', label: 'Sin abrir' }
}

const EQUIPO_COLUMNS = [
  { name: 'Equipo', uid: 'equipo' },
  { name: 'Folio', uid: 'folio' },
  { name: 'Serie', uid: 'serie' },
  { name: 'Sellos', uid: 'sellos' },
  { name: 'Estado', uid: 'estado' },
  { name: '', uid: 'actions' }
]

const fmtFecha = (iso: string | null): string => {
  if (iso === null) return '—'
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}

const RecepcionDetalleView: FC = () => {
  const idSeller = useSelectedSeller()
  const { selectedStore, selectedStoreId } = useLogisticBox()
  const [sello, setSello] = useState<{ equipo: DiscrepancyEquipoDto } | null>(null)
  const [seal, setSeal] = useState('')
  const [seal2, setSeal2] = useState('')
  const [busy, setBusy] = useState(false)

  const data = useApi(useCallback(async () => {
    if (selectedStoreId === null) throw new Error('Selecciona una tienda desde Recepción de cajas')
    return await discrepanciesApi.detail(selectedStoreId, idSeller)
  }, [selectedStoreId, idSeller]), [selectedStoreId, idSeller])

  const abrirSellos = (equipo: DiscrepancyEquipoDto): void => {
    setSello({ equipo })
    setSeal(equipo.seal ?? '')
    setSeal2(equipo.seal2 ?? '')
  }

  const guardarSellos = useCallback(async () => {
    if (sello === null) return
    if (seal.trim() === '') { enqueueSnackbar('El sello es requerido', { variant: 'warning' }); return }
    setBusy(true)
    try {
      await discrepanciesApi.changeSeals(sello.equipo.id_tradein, seal.trim(), seal2.trim() || null, idSeller)
      enqueueSnackbar(`Sellos del equipo ${sello.equipo.id_tradein} actualizados`, { variant: 'success' })
      setSello(null)
      await data.reload()
    } catch (e: any) {
      enqueueSnackbar(e?.message ?? 'No se pudieron actualizar los sellos', { variant: 'error' })
    } finally { setBusy(false) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sello, seal, seal2, idSeller, data])

  return (
    <div className='space-y-4'>
      <BackButton to='discrepancias' />

      <Panel>
        <SectionTitle
          title={`Recepción · ${selectedStore ?? 'Tienda'}`}
          description='Lo que se envió en cada caja vs. lo que realmente llegó'
        />

        <ApiStateView
          loading={data.loading} error={data.error} onRetry={data.reload}
          empty={!data.loading && (data.data?.length ?? 0) === 0} emptyText='Esta tienda no tiene cajas recibidas'
        >
          <div className='space-y-4 mt-4'>
            {(data.data ?? []).map(caja => (
              <CajaBlock key={caja.id_collection} caja={caja} onCambiarSellos={abrirSellos} />
            ))}
          </div>
        </ApiStateView>
      </Panel>

      <Modal isOpen={sello !== null} onClose={() => setSello(null)} size='md'>
        <ModalContent>
          <ModalHeader className='text-[#151930]'>Cambiar sellos · {sello?.equipo.equipo}</ModalHeader>
          <ModalBody>
            <p className='text-xs text-[#151930]/60'>
              Folio {sello?.equipo.folio ?? '—'} · se actualizará el par de sellos para que el equipo se reciba en una próxima caja.
            </p>
            <Input label='Sello 1' value={seal} onValueChange={setSeal} variant='bordered' size='sm' />
            <Input label='Sello 2 (opcional)' value={seal2} onValueChange={setSeal2} variant='bordered' size='sm' />
          </ModalBody>
          <ModalFooter>
            <Button variant='bordered' className='border-gray-200' onPress={() => setSello(null)}>Cancelar</Button>
            <Button isLoading={busy} onPress={() => { guardarSellos() }} className='bg-[#44526B] text-white font-semibold'>
              Guardar sellos
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

const CajaBlock: FC<{ caja: DiscrepancyCajaDto, onCambiarSellos: (e: DiscrepancyEquipoDto) => void }> = ({ caja, onCambiarSellos }) => {
  const conFaltantes = caja.total_discrepancias > 0
  console.log('onCambiarSellos', onCambiarSellos)
  const renderCell = (e: DiscrepancyEquipoDto, columnKey: string): ReactElement => {
    switch (columnKey) {
      case 'equipo':
        return <span className='font-medium text-xs text-[#151930]'>{e.equipo}</span>
      case 'folio':
        return <span className='text-xs text-neutral-700'>{e.folio ?? '—'}</span>
      case 'serie':
        return <span className='text-xs text-neutral-700'>{e.serie ?? '—'}</span>
      case 'sellos':
        return <span className='text-xs text-neutral-700'>{[e.seal, e.seal2].filter(Boolean).join(' · ') || '—'}</span>
      case 'estado': {
        const st = ESTADO[e.estado]
        return <Pill tone={st.tone}>{st.label}</Pill>
      }
      // case 'actions':
      //   return (
      //     <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
      //       {e.estado === 'discrepancia' && (
      //         <Button
      //           size='sm' variant='flat' className='text-xs h-8'
      //           onPress={() => onCambiarSellos(e)}
      //         >
      //           Cambiar sellos
      //         </Button>
      //       )}
      //     </div>
      //   )
      default:
        return <span className='text-xs'>{(e as any)[columnKey]}</span>
    }
  }

  return (
    <div className='border border-[#eef0f7] rounded-xl overflow-hidden'>
      <div className='flex items-center justify-between flex-wrap gap-2 px-4 py-3 bg-[#f9fafc]'>
        <div>
          <div className='font-semibold text-sm text-[#151930]'>
            {caja.folio_caja ?? `Caja ${caja.id_collection}`}
            {caja.tipo_retiro === 'interno' && <span className='ml-2 text-[10px] text-[#37417f] bg-[#EEEDFE] px-1.5 py-0.5 rounded'>Retiro Reuse</span>}
          </div>
          <div className='text-[14px] text-gray-500'>Recibida {fmtFecha(caja.fecha_entregada)}</div>
        </div>
        <div className='flex items-center gap-2 text-xs'>
          <span className='text-[#151930]/60'>{caja.total_recibidos}/{caja.total_enviados} recibidos</span>
          {conFaltantes
            ? <Pill tone='rosa'>● {caja.total_discrepancias} no llegaron</Pill>
            : <Pill tone='green'>✓ Completa</Pill>}
        </div>
      </div>
      <div className='px-2 pb-2'>
        <LogiTable
          ariaLabel={`Equipos · ${caja.folio_caja ?? `Caja ${caja.id_collection}`}`}
          columns={EQUIPO_COLUMNS}
          items={caja.equipos}
          rowKey={(e: DiscrepancyEquipoDto) => e.id_tradein}
          rowsPerPage={0}
          renderCell={renderCell}
          emptyText='Sin equipos en esta caja'
        />
      </div>
    </div>
  )
}

export default RecepcionDetalleView
